import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faPlus, faEdit, faTrash, faSave, faTimes, faSpinner,
  faCertificate, faGraduationCap, faQuestionCircle, faLayerGroup, faSearch, faUsers
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { getExamUrl } from '../utils/slugify';

interface Exam {
  id: number;
  name: string;
  description?: string;
  is_free_available: boolean;
  category?: string;
  subcategory?: string;
  question_count?: number;
  free_question_count?: number;
  is_enabled?: boolean;
  exam_duration_minutes?: number;
  exam_question_count?: number;
  practice_mode_enabled?: boolean;
  badge_image?: string;
  exam_details?: string;
}

const AdminCertifications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_free_available: true,
    category: '',
    subcategory: '',
    is_enabled: true,
    exam_duration_minutes: null as number | null,
    exam_question_count: null as number | null,
    practice_mode_enabled: false,
    badge_image: '',
    exam_details: ''
  });

  useEffect(() => {
    fetchExams();
  }, []);

  // Auto-edit exam if edit query parameter is present
  useEffect(() => {
    const editExamId = searchParams.get('edit');
    if (editExamId && exams.length > 0) {
      const examId = parseInt(editExamId);
      const examToEdit = exams.find(e => e.id === examId);
      if (examToEdit && editingId !== examId) {
        setFormData({
          name: examToEdit.name,
          description: examToEdit.description || '',
          is_free_available: examToEdit.is_free_available,
          category: examToEdit.category || '',
          subcategory: examToEdit.subcategory || '',
          is_enabled: examToEdit.is_enabled !== undefined ? examToEdit.is_enabled : true,
          exam_duration_minutes: examToEdit.exam_duration_minutes || null,
          exam_question_count: examToEdit.exam_question_count || null,
          practice_mode_enabled: examToEdit.practice_mode_enabled || false,
          badge_image: examToEdit.badge_image || '',
          exam_details: examToEdit.exam_details || ''
        });
        setLogoPreview(examToEdit.badge_image ? `/${examToEdit.badge_image}` : null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setEditingId(examId);
        setShowAddForm(true);
        // Scroll to form
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        // Remove query parameter from URL
        setSearchParams({});
      }
    }
  }, [exams, searchParams, setSearchParams, editingId]);

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams');
      setExams(response.data.exams || []);
    } catch (error: any) {
      console.error('Failed to fetch exams:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to load certifications';
      alert(`${errorMessage}. ${error.response?.status === 403 ? 'You may not have admin access.' : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      is_free_available: true,
      category: '',
      subcategory: '',
      is_enabled: true,
      exam_duration_minutes: null,
      exam_question_count: null,
      practice_mode_enabled: false,
      badge_image: '',
      exam_details: ''
    });
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleEdit = (exam: Exam) => {
    setFormData({
      name: exam.name,
      description: exam.description || '',
      is_free_available: exam.is_free_available,
      category: exam.category || '',
      subcategory: exam.subcategory || '',
      is_enabled: exam.is_enabled !== undefined ? exam.is_enabled : true,
      exam_duration_minutes: exam.exam_duration_minutes || null,
      exam_question_count: exam.exam_question_count || null,
      practice_mode_enabled: exam.practice_mode_enabled || false,
      badge_image: exam.badge_image || '',
      exam_details: exam.exam_details || ''
    });
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setEditingId(exam.id);
    setShowAddForm(false);
    
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      alert('Certification name is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        is_free_available: formData.is_free_available,
        category: formData.category.trim() || null,
        subcategory: formData.subcategory.trim() || null,
        is_enabled: formData.is_enabled,
        exam_duration_minutes: formData.exam_duration_minutes || null,
        exam_question_count: formData.exam_question_count || null,
        practice_mode_enabled: formData.practice_mode_enabled,
        badge_image: formData.badge_image.trim() || null,
        exam_details: formData.exam_details.trim() || null
      };

      if (editingId) {
        await api.put(`/exams/${editingId}`, payload);
      } else {
        await api.post('/exams', payload);
      }

      await fetchExams();
      resetForm();
    } catch (error: any) {
      console.error('Failed to save certification:', error);
      alert(error.response?.data?.error || 'Failed to save certification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (examId: number) => {
    const exam = exams.find(e => e.id === examId);
    const questionCount = exam?.question_count || 0;
    
    const message = questionCount > 0
      ? `Are you sure you want to delete "${exam?.name}"? This will also delete ${questionCount} associated question${questionCount !== 1 ? 's' : ''}. This action cannot be undone.`
      : `Are you sure you want to delete "${exam?.name}"? This action cannot be undone.`;

    if (!window.confirm(message)) {
      return;
    }

    setDeleting(examId);
    try {
      await api.delete(`/exams/${examId}`);
      await fetchExams();
    } catch (error: any) {
      console.error('Failed to delete certification:', error);
      alert(error.response?.data?.error || 'Failed to delete certification');
    } finally {
      setDeleting(null);
    }
  };

  // Get unique categories for filter
  const categories = Array.from(new Set(exams.map(e => e.category).filter(Boolean))) as string[];

  // Filter exams
  const filteredExams = exams.filter(exam => {
    const matchesSearch = !searchQuery.trim() || 
      exam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exam.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exam.category?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || exam.category === filterCategory || (!exam.category && filterCategory === 'uncategorized');
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="gp-text-muted">Loading certifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        {/* Header */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="gp-back-link mb-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2 text-xs" />
            {t('common.backToDashboard')}
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <span className="gp-icon-tile-lg">
                  <FontAwesomeIcon icon={faCertificate} className="text-lg" aria-hidden="true" />
                </span>
                <div>
                  <h1 className="gp-page-title">Certification Management</h1>
                  <p className="gp-page-subtitle">
                    {exams.length} certification{exams.length !== 1 ? 's' : ''} total
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              className="gp-btn-primary flex items-center gap-2 px-4 py-2 text-sm"
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
              <span>Add Certification</span>
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <Link
            to="/admin/question"
            className="gp-card gp-card-interactive gp-soft-panel"
          >
              <div className="flex items-center gap-3">
              <span className="gp-icon-tile-lg">
                <FontAwesomeIcon icon={faSearch} className="text-lg" aria-hidden="true" />
              </span>
              <div className="flex-1">
                <h3 className="text-base font-bold mb-1">Search Question</h3>
                <p className="text-xs gp-text-muted">Find and view questions by ID</p>
              </div>
              <FontAwesomeIcon icon={faArrowRight} className="text-sm gp-text-muted" aria-hidden="true" />
            </div>
          </Link>
          <Link
            to="/admin/users"
            className="gp-card gp-card-interactive gp-soft-panel"
          >
            <div className="flex items-center gap-3">
              <span className="gp-icon-tile-lg">
                <FontAwesomeIcon icon={faUsers} className="text-lg" aria-hidden="true" />
              </span>
              <div className="flex-1">
                <h3 className="text-base font-bold mb-1">User Management</h3>
                <p className="text-xs gp-text-muted">Manage users and permissions</p>
              </div>
              <FontAwesomeIcon icon={faArrowRight} className="text-sm gp-text-muted" aria-hidden="true" />
            </div>
          </Link>
        </div>

        {/* Search and Filter */}
        <div className="gp-card mb-5">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 gp-text-muted text-sm"
              />
              <input
                type="text"
                placeholder="Search certifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="gp-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faLayerGroup} className="gp-text-muted text-sm" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="gp-select"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                <option value="uncategorized">Uncategorized</option>
              </select>
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || editingId !== null) && (
          <div ref={formRef} className="gp-card mb-5 animate-fade-in">
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <div className="flex items-center gap-2.5">
                <h2 className="text-lg font-bold">
                  {editingId ? 'Edit Certification' : 'Add New Certification'}
                </h2>
                {editingId && (
                  <span className="px-2 py-1 rounded text-xs font-semibold">
                    ID: {editingId}
                  </span>
                )}
              </div>
              <button
                onClick={resetForm}
                className="gp-text-muted"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-3">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Certification Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="e.g., AWS Solutions Architect Associate"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder="Enter a description for this certification..."
                />
              </div>

              {/* Badge Image / Logo */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Certification Logo
                </label>
                
                {/* File Upload Section */}
                <div className="mb-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        alert('Please select an image file');
                        return;
                      }

                      // Validate file size (5MB)
                      if (file.size > 5 * 1024 * 1024) {
                        alert('File size must be less than 5MB');
                        return;
                      }

                      setUploadingLogo(true);
                      try {
                        const uploadFormData = new FormData();
                        uploadFormData.append('logo', file);

                        const response = await api.post('/upload/certification-logo', uploadFormData, {
                          headers: {
                            'Content-Type': 'multipart/form-data',
                          },
                        });

                        if (response.data.success) {
                          setFormData({ ...formData, badge_image: response.data.filename });
                          // Create preview URL
                          const previewUrl = URL.createObjectURL(file);
                          setLogoPreview(previewUrl);
                        } else {
                          alert('Failed to upload logo');
                        }
                      } catch (error: any) {
                        console.error('Upload error:', error);
                        alert(error.response?.data?.error || 'Failed to upload logo');
                      } finally {
                        setUploadingLogo(false);
                      }
                    }}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="gp-btn-primary text-sm disabled:opacity-50"
                  >
                    {uploadingLogo ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPlus} className="text-xs" />
                        <span>Upload Logo</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Manual Filename Input */}
                <div className="mb-2">
                  <label className="block text-xs font-medium mb-1">
                    Or enter filename manually:
                  </label>
                  <input
                    type="text"
                    value={formData.badge_image}
                    onChange={(e) => {
                      setFormData({ ...formData, badge_image: e.target.value });
                      setLogoPreview(null);
                    }}
                    className="input-field"
                    placeholder="e.g., saa-c03.png"
                  />
                </div>

                {/* Preview */}
                {(formData.badge_image || logoPreview) && (
                  <div className="mt-3 p-3 border rounded-lg">
                    <p className="text-xs font-medium mb-2">Preview:</p>
                    <div className="flex items-center gap-3">
                      <img 
                        src={logoPreview || `/${formData.badge_image}`} 
                        alt="Logo preview" 
                        className="w-24 h-24 object-contain border rounded p-2"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      {formData.badge_image && (
                        <div className="flex-1">
                          <p className="text-xs gp-text-muted">
                            <strong>Filename:</strong> {formData.badge_image}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, badge_image: '' });
                              setLogoPreview(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                            className="mt-2 text-xs font-medium"
                            style={{ color: '#b91c1c' }}
                          >
                            Remove logo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Exam Details */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Exam Details <span className="text-xs gp-text-muted">(Markdown supported)</span>
                </label>
                <textarea
                  value={formData.exam_details}
                  onChange={(e) => setFormData({ ...formData, exam_details: e.target.value })}
                  className="input-field min-h-[150px]"
                  placeholder="Enter exam details that will be displayed below the practice cards on the exam page. Supports Markdown formatting."
                  rows={6}
                />
                <p className="text-xs gp-text-muted mt-1">
                  This content will be displayed below the practice mode cards on the exam page. Supports Markdown formatting.
                </p>
              </div>

              {/* Category and Subcategory */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input-field"
                    placeholder="e.g., AWS, PMP, Cloud"
                    list="category-suggestions"
                  />
                  <datalist id="category-suggestions">
                    {categories.map(cat => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Subcategory <span className="text-xs gp-text-muted">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    className="input-field"
                    placeholder="e.g., Foundational, Associate, Professional, Specialty"
                  />
                </div>
              </div>

              {/* Free Tier and Enabled Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_free_available}
                      onChange={(e) => setFormData({ ...formData, is_free_available: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-semibold">
                      Free Tier Available
                    </span>
                  </label>
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-semibold">
                      Enabled (Visible on Website)
                    </span>
                  </label>
                </div>
              </div>

              {/* Exam Configuration Section */}
              <div className="pt-4 border-t">
                <div className="mb-3">
                  <h3 className="text-sm font-bold mb-1">Practice Exam Settings</h3>
                  <p className="text-xs gp-text-muted">
                    Configure practice exams to match real exam conditions (time limit and question count)
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Exam Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.exam_duration_minutes || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        exam_duration_minutes: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="input-field"
                      placeholder="e.g., 180"
                    />
                    <p className="text-xs gp-text-muted mt-1">
                      Real exam time limit
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Exam Question Count
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.exam_question_count || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        exam_question_count: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="input-field"
                      placeholder="e.g., 75"
                    />
                    <p className="text-xs gp-text-muted mt-1">
                      Number of questions in real exam
                    </p>
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.practice_mode_enabled}
                        onChange={(e) => setFormData({ ...formData, practice_mode_enabled: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm font-semibold">
                        Enable Exam-Like Practice Mode
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2.5 pt-4 border-t">
                <button
                  onClick={resetForm}
                  className="gp-btn-ghost text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="gp-btn-primary flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="text-xs" />
                      <span>Save Certification</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Certifications List */}
        <div className="gp-card">
          {filteredExams.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faCertificate} className="text-4xl gp-text-muted mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchQuery || filterCategory !== 'all' ? 'No Certifications Found' : 'No Certifications Yet'}
              </h3>
              <p className="text-sm gp-text-muted mb-4">
                {searchQuery || filterCategory !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by adding your first certification'}
              </p>
              {!searchQuery && filterCategory === 'all' && (
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddForm(true);
                  }}
                  className="gp-btn-primary inline-flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-xs" />
                  <span>Add First Certification</span>
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredExams.map((exam) => {
                const isEditing = editingId === exam.id;

                return (
                  <div
                    key={exam.id}
                    className={`p-3.5 rounded-md transition-all ${ isEditing ? 'gp-soft-panel' : '' }`}
                    style={{ border: isEditing ? '1px solid var(--gp-brand)' : '1px solid var(--gp-border)' }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold gp-text-muted">
                            ID: {exam.id}
                          </span>
                          {exam.category && (
                            <>
                              <span className="gp-text-muted">•</span>
                              <span
                                className="px-2 py-0.5 rounded text-xs"
                                style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }}
                              >
                                {exam.category}{exam.subcategory ? ` - ${exam.subcategory}` : ''}
                              </span>
                            </>
                          )}
                          {exam.is_enabled === false ? (
                            <span
                              className="px-2 py-0.5 rounded text-xs font-semibold"
                              style={{ background: '#fef2f2', color: '#b91c1c' }}
                            >
                              Disabled
                            </span>
                          ) : (
                            <span
                              className="px-2 py-0.5 rounded text-xs font-semibold"
                              style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}
                            >
                              Enabled
                            </span>
                          )}
                          {exam.is_free_available && (
                            <span className="gp-tag-free">Free</span>
                          )}
                          {exam.question_count !== undefined && (
                            <>
                              <span className="gp-text-muted">•</span>
                              <span
                                className="px-2 py-0.5 rounded text-xs"
                                style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }}
                              >
                                {exam.question_count} {exam.question_count === 1 ? 'question' : 'questions'}
                              </span>
                            </>
                          )}
                          {exam.free_question_count !== undefined && exam.free_question_count > 0 && (
                            <>
                              <span className="gp-text-muted">•</span>
                              <span
                                className="px-2 py-0.5 rounded text-xs font-semibold"
                                style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}
                              >
                                {exam.free_question_count} free
                              </span>
                            </>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold mb-2">
                          {exam.name}
                        </h3>
                        {exam.description && (
                          <p className="text-sm gp-text-muted mb-3">
                            {exam.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap gap-2">
                          <Link
                            to={`/admin/questions/${exam.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 gp-btn-primary text-sm"
                          >
                            <FontAwesomeIcon icon={faQuestionCircle} className="text-sm" />
                            <span>Manage Questions</span>
                            {exam.question_count !== undefined && (
                              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">
                                {exam.question_count}
                              </span>
                            )}
                            {exam.free_question_count !== undefined && exam.free_question_count > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}>
                                {exam.free_question_count} free
                              </span>
                            )}
                          </Link>
                          <Link
                            to={getExamUrl(exam.id, exam.name)}
                            className="gp-btn-ghost text-xs"
                          >
                            <FontAwesomeIcon icon={faGraduationCap} className="text-xs" />
                            <span>View Exam</span>
                          </Link>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          type="button"
                          onClick={() => handleEdit(exam)}
                          className="gp-btn-primary text-xs"
                        >
                          <FontAwesomeIcon icon={faEdit} className="text-xs" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(exam.id)}
                          disabled={deleting === exam.id}
                          className="gp-btn-ghost text-xs disabled:opacity-50"
                          style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                        >
                          {deleting === exam.id ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTrash} className="text-xs" />
                              <span>Delete</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCertifications;
